import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { Fragment } from 'react';
import { window } from '../../helpers/BrowserHelpers';
import BlockTypes from '../../types/BlockTypes';
import { normalizeNode, shouldNotBeEmpty, shouldBeAtRoot, shouldMergeWithSimilarSiblings, shouldHaveChildrenOfType } from '../utils/NormalizationHelper';
import renderBlocks from '../utils/renderBlocks';
import makeToolbarButton, { BASIC_INSERTION, EXPANDED_INSERTION } from '../utils/makeToolbarButton';
import makeKeyboardCommandShortcut from '../utils/makeKeyboardCommandShortcut';
import createStore from '../utils/createStore';
import { TableIcon } from '../../components/Icons';
import DocumentEventListener from '../../components/DocumentEventListener';
import isSelectionAroundTable from './queries/isSelectionAroundTable';
import isSelectionInTable from './queries/isSelectionInTable';
import isSelectionInLastTableCell from './queries/isSelectionInLastTableCell';
import isSelectionInLastTableRow from './queries/isSelectionInLastTableRow';
import isTableCellsEmpty from './queries/isTableCellsEmpty';
import isTableEmpty from './queries/isTableEmpty';
import isTableExpanded from './queries/isTableExpanded';
import isTableResizing from './queries/isTableResizing';
import isValidSelectionRange from './queries/isValidSelectionRange';
import canAddTableColumn from './queries/canAddTableColumn';
import canAddTableRow from './queries/canAddTableRow';
import canRemoveTableColumn from './queries/canRemoveTableColumn';
import canRemoveTableRow from './queries/canRemoveTableRow';
import getCurrentTableId from './queries/getCurrentTableId';
import getFragmentFromTableSelection from './queries/getFragmentFromTableSelection';
import getTable from './queries/getTable';
import getTableCells from './queries/getTableCells';
import getTableRowCells from './queries/getTableRowCells';
import getTableWidth from './queries/getTableWidth';
import getSelectedTableCells from './queries/getSelectedTableCells';
import getCellCoords from './queries/getCellCoords';
import getCellsActiveMarks from './queries/getCellsActiveMarks';
import getCellsCoords from './queries/getCellsCoords';
import getCellAbove from './queries/getCellAbove';
import getCellBelow from './queries/getCellBelow';
import getCellOnTheRight from './queries/getCellOnTheRight';
import getCellOnTheLeft from './queries/getCellOnTheLeft';
import getTableSelectRange from './queries/getTableSelectRange';
import shouldScrollToIndex from './queries/shouldScrollToIndex';
import getTableColumnCountForCells from './utils/getTableColumnCountForCells';
import getTableRowCountForCells from './utils/getTableRowCountForCells';
import insertTable from './commands/insertTable';
import removeTable from './commands/removeTable';
import moveToCellAbove from './commands/moveToCellAbove';
import moveToCellBelow from './commands/moveToCellBelow';
import moveToRowBelow from './commands/moveToRowBelow';
import addTableColumn from './commands/addTableColumn';
import addTableColumnAfter from './commands/addTableColumnAfter';
import addTableColumnBefore from './commands/addTableColumnBefore';
import addTableColumns from './commands/addTableColumns';
import addTableRow from './commands/addTableRow';
import addTableRowBelow from './commands/addTableRowBelow';
import addTableRows from './commands/addTableRows';
import moveTableRow from './commands/moveTableRow';
import moveTableColumn from './commands/moveTableColumn';
import mergeCurrentTableWithFragment from './commands/mergeCurrentTableWithFragment';
import removeTableColumn from './commands/removeTableColumn';
import removeTableRow from './commands/removeTableRow';
import selectTable from './commands/selectTable';
import selectTableRow from './commands/selectTableRow';
import selectTableColumn from './commands/selectTableColumn';
import selectTableCell from './commands/selectTableCell';
import selectTableCellRange from './commands/selectTableCellRange';
import deselectTable from './commands/deselectTable';
import refreshSelectedCellKeys from './commands/refreshSelectedCellKeys';
import emptyTableCell from './commands/emptyTableCell';
import emptyTableCells from './commands/emptyTableCells';
import emptyTableCellsAtSelection from './commands/emptyTableCellsAtSelection';
import toggleMarkOnTableSelection from './commands/toggleMarkOnTableSelection';
import setTableColumnWidth from './commands/setTableColumnWidth';
import expandTable from './commands/expandTable';
import reduceTable from './commands/reduceTable';
import setHoveredColumn from './commands/setHoveredColumn';
import setHoveredRow from './commands/setHoveredRow';
import setHoveredColumnControls from './commands/setHoveredColumnControls';
import setHoveredRowControls from './commands/setHoveredRowControls';
import registerScroll from './commands/registerScroll';
import makeOnCopy from './handlers/onCopy';
import makeOnCut from './handlers/onCut';
import makeOnPaste from './handlers/onPaste';
import makeOnKeyDown from './handlers/onKeyDown';
import onChange from './handlers/onChange';
import onCommand from './handlers/onCommand';
import Table from './components/Table';
import TableCell from './components/TableCell';
import Styles from './components/Styles';
import serializers from './serializers';
import deserializers from './deserializers';
import './style.css';
import './mobile.css';

function siblingsComparator(a, b) {
  return a.type === b.type && a.getIn(['nodes', 0, 'data', 'tableId']) === b.getIn(['nodes', 0, 'data', 'tableId']);
}

export default (function () {
  var _renderBlocks;

  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$isMobile = _ref.isMobile,
      isMobile = _ref$isMobile === void 0 ? false : _ref$isMobile;

  var store = createStore({
    hoveredColumn: null,
    hoveredRow: null,
    hoveredColumnControls: null,
    hoveredRowControls: null
  });
  var documentEventHandlers = {};

  var _makeOnCopy = makeOnCopy(store),
      onDocumentCopy = _makeOnCopy.onDocumentCopy,
      onCopy = _makeOnCopy.onCopy;

  var _makeOnCut = makeOnCut(store),
      onDocumentCut = _makeOnCut.onDocumentCut,
      onCut = _makeOnCut.onCut;

  var _makeOnPaste = makeOnPaste(store),
      onDocumentPaste = _makeOnPaste.onDocumentPaste,
      onPaste = _makeOnPaste.onPaste;

  var _makeOnKeyDown = makeOnKeyDown(store),
      onDocumentKeyDown = _makeOnKeyDown.onDocumentKeyDown,
      onKeyDown = _makeOnKeyDown.onKeyDown;

  return [normalizeNode(BlockTypes.TABLE, [shouldNotBeEmpty, shouldBeAtRoot, [shouldHaveChildrenOfType, BlockTypes.TABLE_CELL], [shouldMergeWithSimilarSiblings, siblingsComparator], function shouldHaveChildrenWithSameTableId(node, editor) {
    var tableId = node.getIn(['nodes', 0, 'data', 'tableId']);
    var firstIllegitimateCellIndex = node.nodes.findIndex(function (cell) {
      return cell.getIn(['data', 'tableId']) !== tableId;
    });

    if (firstIllegitimateCellIndex > 0) {
      node.nodes.skip(firstIllegitimateCellIndex).forEach(function (cell) {
        editor.unwrapNodeByKey(cell.key);
        cell.normalize(editor.controller, node);
      });
    }
  }]), _objectSpread({
    serializers: serializers,
    deserializers: deserializers,
    queries: _objectSpread({
      isSelectionAroundTable: isSelectionAroundTable,
      isSelectionInTable: isSelectionInTable,
      isSelectionInLastTableCell: isSelectionInLastTableCell,
      isSelectionInLastTableRow: isSelectionInLastTableRow,
      isTableCellsEmpty: isTableCellsEmpty,
      isTableEmpty: isTableEmpty,
      isTableExpanded: isTableExpanded
    }, isTableResizing(store), {
      isValidSelectionRange: isValidSelectionRange,
      canAddTableColumn: canAddTableColumn,
      canAddTableRow: canAddTableRow,
      canRemoveTableColumn: canRemoveTableColumn,
      canRemoveTableRow: canRemoveTableRow
    }, getFragmentFromTableSelection(store), {
      getCurrentTableId: getCurrentTableId,
      getTable: getTable,
      getTableCells: getTableCells,
      getTableRowCells: getTableRowCells
    }, getSelectedTableCells(store), {}, getTableWidth(store), {
      getCellsActiveMarks: getCellsActiveMarks,
      getCellCoords: getCellCoords
    }, getCellsCoords(store), {
      getTableRowCount: function getTableRowCount(editor, tableId) {
        return getTableRowCountForCells(editor.getTableCells(tableId));
      },
      getTableColumnCount: function getTableColumnCount(editor, tableId) {
        return getTableColumnCountForCells(editor.getTableCells(tableId));
      },
      getCellAbove: getCellAbove,
      getCellBelow: getCellBelow,
      getCellOnTheRight: getCellOnTheRight,
      getCellOnTheLeft: getCellOnTheLeft
    }, getTableSelectRange(store), {}, shouldScrollToIndex(store)),
    commands: _objectSpread({
      // Moving selection
      moveToCellBelow: moveToCellBelow,
      moveToRowBelow: moveToRowBelow,
      moveToCellAbove: moveToCellAbove
    }, selectTable(store), {}, selectTableRow(store), {}, selectTableColumn(store), {}, selectTableCell(store), {}, selectTableCellRange(store), {}, deselectTable(store), {}, refreshSelectedCellKeys(store), {
      // Table block operations
      insertTable: insertTable,
      removeTable: removeTable,
      addTableColumn: addTableColumn,
      addTableColumnAfter: addTableColumnAfter,
      addTableColumnBefore: addTableColumnBefore,
      addTableColumns: addTableColumns,
      addTableRow: addTableRow,
      addTableRowBelow: addTableRowBelow,
      addTableRows: addTableRows,
      moveTableRow: moveTableRow,
      moveTableColumn: moveTableColumn,
      removeTableColumn: removeTableColumn,
      removeTableRow: removeTableRow,
      mergeCurrentTableWithFragment: mergeCurrentTableWithFragment
    }, toggleMarkOnTableSelection(store), {
      emptyTableCell: emptyTableCell,
      emptyTableCells: emptyTableCells,
      emptyTableCellsAtSelection: emptyTableCellsAtSelection,
      setTableColumnWidth: setTableColumnWidth,
      expandTable: expandTable,
      reduceTable: reduceTable
    }, setHoveredColumn(store), {}, setHoveredRow(store), {}, setHoveredColumnControls(store), {}, setHoveredRowControls(store), {}, registerScroll(store)),
    onConstruct: function onConstruct(editor, next) {
      store.reset();
      documentEventHandlers = {
        onCopy: function onCopy(event) {
          return onDocumentCopy(event, editor);
        },
        onCut: function onCut(event) {
          return onDocumentCut(event, editor);
        },
        onPaste: function onPaste(event) {
          return onDocumentPaste(event, editor);
        },
        onKeyDown: function onKeyDown(event) {
          return onDocumentKeyDown(event, editor);
        },
        onMouseDown: function onMouseDown(event) {
          if (editor.isEventInEditor(event)) return;
          editor.deselectTable();
        }
      };
      return next();
    },
    onSelect: function onSelect(event, editor, next) {
      var selection = window.getSelection();

      if (selection.rangeCount > 0) {
        return next();
      }
    },
    onCopy: onCopy,
    onCut: onCut,
    onPaste: onPaste
  }, onChange(store), {
    onKeyDown: onKeyDown,
    onCommand: onCommand,
    renderEditor: function renderEditor(props, editor, next) {
      return React.createElement(Fragment, null, next(), React.createElement(Styles, {
        store: store
      }), React.createElement(DocumentEventListener, documentEventHandlers));
    }
  }), renderBlocks((_renderBlocks = {}, _defineProperty(_renderBlocks, BlockTypes.TABLE, function (props) {
    return React.createElement(Table, Object.assign({
      store: store,
      isMobile: isMobile
    }, props));
  }), _defineProperty(_renderBlocks, BlockTypes.TABLE_CELL, function (props) {
    return React.createElement(TableCell, Object.assign({
      isMobile: isMobile,
      store: store
    }, props));
  }), _renderBlocks)), makeToolbarButton(BlockTypes.TABLE_CELL, {
    icon: TableIcon,
    title: 'Table',
    alias: ['array'],
    visibility: BASIC_INSERTION | EXPANDED_INSERTION,
    canApply: function canApply(editor) {
      return editor.isMutableBlock();
    },
    onApply: function onApply(editor) {
      return editor.insertTable();
    }
  }), makeKeyboardCommandShortcut('mod+alt+t', {
    type: BlockTypes.TABLE_CELL,
    canApply: function canApply(editor) {
      return editor.isMutableBlock();
    },
    command: 'insertTable',
    byKey: false
  })];
});